import { ReactNode } from "react";

type WidgetShellProps = {
    /** Short uppercase label shown in the tile header (already translated). */
    label: string;
    /** Optional right-aligned status text, e.g. "live" / "cached" (already translated). */
    status?: string;
    /** Optional right-aligned controls sharing the header row with the label. */
    actions?: ReactNode;
    children: ReactNode;
};

/**
 * Shared "live dashboard tile" chrome for the Widgets section: a pulsing
 * status dot + a monospace uppercase label, a divider, then the widget body.
 * Keeps GithubStats, BlockMiner and AgentLoopLab visually consistent.
 *
 * The dot is theme-primary (charcoal / light-gray) rather than a green accent:
 * the site's palette is monochromatic and reserves colour for animations only,
 * so the pulse alone carries the "live" semantics without a hue that clashes
 * with the surrounding UI.
 */
export default function WidgetShell({ label, status, actions, children }: WidgetShellProps) {
    return (
        <div>
            <div className="flex min-h-8 items-center justify-between gap-3 border-b border-subtle pb-3 mb-4">
                <div className="flex items-center gap-2">
                    <span
                        className="size-1.5 shrink-0 bg-accent motion-safe:animate-pulse"
                        aria-hidden="true"
                    />
                    <span className="font-mono text-xs uppercase tracking-wider text-theme-light">
                        {label}
                    </span>
                </div>
                {status && (
                    <span className="font-mono text-[10px] uppercase tracking-wider text-theme-light opacity-70">
                        {status}
                    </span>
                )}
                {actions}
            </div>
            {children}
        </div>
    );
}
